import { useEffect, useState } from "react";
import { useReminderDeleteMutation } from "web_myplann/Api/Reminders/useReminderDeleteMutation";
import { reminderRowAnchorId } from "web_myplann/NotificationCenter/Utils/reminderScrollParam";
import { ReminderActionsDrawer } from "web_myplann/Reminders/Components/ReminderActionsDrawer";
import { ReminderCreateDrawer } from "web_myplann/Reminders/Components/ReminderCreateDrawer";
import { ReminderEditDrawer } from "web_myplann/Reminders/Components/ReminderEditDrawer";
import { ReminderSnoozeDrawer } from "web_myplann/Reminders/Components/ReminderSnoozeDrawer";
import { RemindersCardList } from "web_myplann/Reminders/Components/RemindersCardList";
import { RemindersFlatList } from "web_myplann/Reminders/Components/RemindersFlatList";
import { useScrollToReminderRow } from "web_myplann/Reminders/Hooks/useScrollToReminderRow";
import type {
	Reminder,
	ReminderEntity,
	RemindersSectionVariant,
} from "web_myplann/Reminders/Types/ReminderTypes";
import { useAutoResetState } from "web_myplann/Shared/Hooks/useAutoResetState";
import { REMINDER_HIGHLIGHT_DURATION_MS } from "web_myplann/Shared/Utils/reminder";

type ActiveDrawer =
	| { type: "none" }
	| { type: "create" }
	| { type: "edit"; reminder: Reminder }
	| { type: "actions"; reminder: Reminder }
	| { type: "snooze"; reminder: Reminder };

type Props = {
	entity: ReminderEntity;
	reminders: Reminder[] | undefined;
	isLoading: boolean;
	isError: boolean;
	variant?: RemindersSectionVariant;
};

export const RemindersSectionView = ({
	entity,
	reminders = [],
	isLoading,
	isError,
	variant = "card",
}: Props) => {
	const [active, setActive] = useState<ActiveDrawer>({ type: "none" });

	const deepLinkHighlightedId = useScrollToReminderRow(!isLoading && !isError);

	const close = () => setActive({ type: "none" });

	const { mutate: deleteReminder } = useReminderDeleteMutation();

	const remove = (reminder: Reminder) => {
		close();
		deleteReminder(reminder.id);
	};

	const [highlightedId, highlight] = useAutoResetState<string | null>(
		null,
		REMINDER_HIGHLIGHT_DURATION_MS,
	);

	useEffect(() => {
		if (highlightedId === null) {
			return;
		}
		document
			.getElementById(reminderRowAnchorId(highlightedId))
			?.scrollIntoView({ behavior: "smooth", block: "center" });
	}, [highlightedId]);

	const openExisting = (reminder: Reminder) => {
		close();
		highlight(reminder.id);
	};

	const activeHighlightedId = highlightedId ?? deepLinkHighlightedId;

	if (isError) {
		return null;
	}

	return (
		<>
			{variant === "flat" ? (
				<RemindersFlatList
					reminders={reminders}
					isLoading={isLoading}
					highlightedId={activeHighlightedId}
					onAdd={() => setActive({ type: "create" })}
					onTap={(reminder) => setActive({ type: "actions", reminder })}
					onRemove={remove}
				/>
			) : (
				<RemindersCardList
					reminders={reminders}
					isLoading={isLoading}
					highlightedId={activeHighlightedId}
					onAdd={() => setActive({ type: "create" })}
					onTap={(reminder) => setActive({ type: "actions", reminder })}
					onRemove={remove}
				/>
			)}

			{active.type === "create" && (
				<ReminderCreateDrawer
					entity={entity}
					existingReminders={reminders}
					open
					onClose={close}
					onOpenExisting={openExisting}
				/>
			)}

			{active.type === "edit" && (
				<ReminderEditDrawer
					entity={entity}
					reminder={active.reminder}
					open
					onClose={close}
				/>
			)}

			{active.type === "actions" && (
				<ReminderActionsDrawer
					reminder={active.reminder}
					open
					onClose={close}
					onEdit={() => setActive({ type: "edit", reminder: active.reminder })}
					onSnooze={() =>
						setActive({ type: "snooze", reminder: active.reminder })
					}
					onDelete={() => remove(active.reminder)}
				/>
			)}

			{active.type === "snooze" && (
				<ReminderSnoozeDrawer reminder={active.reminder} open onClose={close} />
			)}
		</>
	);
};
